/**
 * TrailMetaForm — captures descriptive metadata after a GPS track is stopped.
 *
 * This is now a thin caller of the shared glass-form system
 * (src/components/glassForm.js), which is BisaWofa's design principle for all
 * modal forms. The field set will grow over time — add entries to FIELDS and a
 * matching column in gps_trail_meta (see database.js).
 *
 * Usage:
 *   const data = await openTrailMetaForm({ trailId, summary });
 *   if (data) await saveGpsTrailMeta({ trailId, ...data });
 *
 * Resolves with:
 *   { trackedBy, localArea, localName, surface, trackedAt }  on Save
 *   null                                                     on Skip / dismiss
 */

import { openGlassModal } from './glassForm.js';

const SURFACE_OPTIONS = [
  '',           // → "Select surface…"
  'Paved / Tarmac',
  'Concrete',
  'Gravel',
  'Dirt / Earth',
  'Sand',
  'Grass',
  'Cobblestone',
  'Mixed',
  'Other',
];

/**
 * Open the metadata form.
 * @param {object} ctx
 * @param {number} [ctx.trailId]   the trail this metadata describes
 * @param {object} [ctx.summary]   { pointCount, distanceM } for the subtitle
 * @returns {Promise<null | {trackedBy:string, localArea:string, localName:string, surface:string, trackedAt:string}>}
 */
export async function openTrailMetaForm(ctx = {}) {
  const trackedAtIso = new Date().toISOString();

  const subtitleBits = [];
  if (ctx.summary?.pointCount != null) subtitleBits.push(`${ctx.summary.pointCount} points`);
  if (ctx.summary?.distanceM != null) {
    const d = ctx.summary.distanceM < 1000
      ? `${Math.round(ctx.summary.distanceM)} m`
      : `${(ctx.summary.distanceM / 1000).toFixed(2)} km`;
    subtitleBits.push(d);
  }

  const result = await openGlassModal({
    title: 'Track Details',
    subtitle: subtitleBits.length ? subtitleBits.join(' · ') : 'Track recorded',
    icon: 'bi-signpost-2',
    submitLabel: 'Save details',
    cancelLabel: 'Skip',
    fields: [
      { name: 'trackedBy', label: 'Tracked by', type: 'text', placeholder: 'Your name', maxlength: 120, autocomplete: 'name' },
      { name: 'localArea', label: 'Local area', type: 'text', placeholder: 'District / region', maxlength: 160 },
      { name: 'localName', label: 'Local name of area', type: 'text', placeholder: 'What locals call this place', maxlength: 160 },
      { name: 'surface',   label: 'Surface of track', type: 'select', options: SURFACE_OPTIONS, placeholder: 'Select surface…' },
      { name: 'trackedAtDisplay', label: 'Tracked at', type: 'readonly', value: _formatTimestamp(trackedAtIso), excludeFromResult: true },
    ],
  });

  if (!result) return null;
  // Replace the human-readable display value with the canonical ISO timestamp.
  return {
    trackedBy: result.trackedBy || '',
    localArea: result.localArea || '',
    localName: result.localName || '',
    surface:   result.surface || '',
    trackedAt: trackedAtIso,
  };
}

function _formatTimestamp(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
}

export default { openTrailMetaForm };
